import { ChevronRight, HelpCircle, Star } from 'lucide-react';

import { Button } from '@/components/shadcn/ui/button';
import { useFeedbackTerminology } from '@/entities/workspace/hooks';
import { openSignalFilterFeedbackPreviewDialog } from '@/features/(signal)/signal-issue-detail/ui/signal-feedback-preview-dialog';
import { useIssueListActions } from '@/features/(signal)/signals/hooks';
import {
  getPeriodLabel,
  getPreviousPeriodDescription,
} from '@/features/(signal)/signals/lib';
import type {
  CompositedSignalIssue,
  SignalDateSelectionInput,
} from '@/features/(signal)/signals/model';
import { ChangesLabel } from '@/features/(signal)/signals/ui/changes-label';
import { SignalBadge } from '@/features/(signal)/signals/ui/signal-badge';
import { cn } from '@/lib/cn';
import { Tooltip } from '@/shared/ui';
import { formatNumber } from '@/utils/util';

import { IssueDetailSentimentRatio } from './issue-detail-sentiment-ratio';

export const IssueDetailPropertyArea = (props: {
  issue: CompositedSignalIssue;
  filterSet: StructuredFilter;
  selectedPeriodFilterSet: StructuredFilter;
  datePreset: SignalDateSelectionInput;
}) => {
  const { replaceFeedbackTerminology } = useFeedbackTerminology();
  const { favoriteIssue } = useIssueListActions();
  const { issue } = props;

  const onViewDataClick = () => {
    openSignalFilterFeedbackPreviewDialog({
      title: `${issue.name} ${replaceFeedbackTerminology('feedbacks')}`,
      queryKey: [
        'signal',
        'detail',
        issue.name,
        props.datePreset,
        JSON.stringify(props.selectedPeriodFilterSet),
      ],
      payload: props.selectedPeriodFilterSet,
    });
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Title */}
      <div className="flex items-center mb-7 gap-5">
        <h1 className="text-[40px] font-bold text-gray-900">{issue.name}</h1>
        <Button
          variant="outline"
          size="icon-lg"
          onClick={() => favoriteIssue(issue.name, !issue.favorite)}
        >
          <Star
            className={cn('size-4 text-gray-600 fill-none', {
              'text-yellow-500 fill-yellow-500': issue.favorite,
            })}
          />
        </Button>
      </div>

      {/* Property Rows */}
      <div className="flex flex-col space-y-4">
        {/* Feedbacks Row */}
        <div className="flex items-center">
          <span className="text-base font-normal text-gray-700 w-44">
            {replaceFeedbackTerminology('Feedback count')}
          </span>
          <div className="flex items-center gap-2 flex-1">
            <Button
              variant="ghost"
              className="-ml-2"
              size="sm"
              onClick={onViewDataClick}
            >
              <span className="text-base text-gray-900">
                {formatNumber(issue.selectedPeriodCount)}
              </span>
              <ChevronRight className="size-3.5 text-muted-foreground" />
            </Button>
          </div>
        </div>
        {/* % Changes Row */}
        <div className="flex items-center">
          <div className="flex items-center gap-1 shrink-0 w-44">
            <span className="text-base text-gray-700">
              {getPeriodLabel(props.datePreset)} change
            </span>
            <Tooltip
              content={
                <p className="whitespace-pre-line">
                  {replaceFeedbackTerminology(
                    `Comparison of feedback counts between\n the current ${getPreviousPeriodDescription(props.datePreset)} and the previous ${getPreviousPeriodDescription(props.datePreset)}`,
                  )}
                </p>
              }
            >
              <HelpCircle className="size-3.5 text-muted-foreground" />
            </Tooltip>
          </div>
          <ChangesLabel
            className="text-base"
            comparisonPeriodCount={issue.comparisonPeriodCount}
            selectedPeriodCount={issue.selectedPeriodCount}
            datePreset={props.datePreset}
          />
        </div>
        {/* Signals Row */}
        <div className="flex items-center">
          <span className="text-base text-gray-700 w-44">Signals</span>
          <div className="flex items-center gap-2 flex-1 flex-wrap">
            {issue.signalTitles.length === 0 ? (
              <span className="text-base text-gray-400">-</span>
            ) : (
              issue.signalTitles.map((title, idx) => (
                <SignalBadge
                  key={issue.signalIds[idx] ?? idx}
                  title={title}
                  tooltip={title}
                  className="max-w-none"
                />
              ))
            )}
          </div>
        </div>
        {/* % Sentiments raito Row */}
        <div className="flex items-center ">
          <span className="text-base font-normal text-gray-700 w-44 flex items-center gap-0">
            Sentiments ratio
          </span>
          <IssueDetailSentimentRatio
            issue={issue}
            datePreset={props.datePreset}
            filterSet={props.filterSet}
          />
        </div>
      </div>
    </div>
  );
};
